import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server'
import { Divider, message, Modal, Trans } from '@/components'
import {
  Instance,
  ReleasedStatuses,
  RunningStatuses,
  SnapshottingStatuses,
} from '@/models/instance'

import { useCancelSnapshot } from './useCancelSnapshot'
import { useInstanceListQuery } from './useInstanceListQuery'

export interface FilterItem {
  label: ReactNode
  value: string
  codes: number[]
}

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3

export const showSnapshotBtnMap: Record<number, boolean> = {
  30: false, // image id
  27: false,
}

export function useInstanceList() {
  const { t } = useTranslation()

  const statusFilters: FilterItem[] = [
    {
      label: <Trans i18nKey="instance.list.statusLabelRunning">Running</Trans>,
      value: 'active',
      codes: RunningStatuses,
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelSnapshotting">Snapshotting</Trans>,
      value: 'snapshotting',
      codes: SnapshottingStatuses,
    },
    {
      label: <Trans i18nKey="instance.list.statusLabelReleased">Released</Trans>,
      value: 'released',
      codes: ReleasedStatuses,
    },
  ]

  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])
  const [page, setPage] = useState(1)
  const [createSnapshotInstance, setCreateSnapshotInstance] = useState<Instance | undefined>()
  const pageSize = 10
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)

  const [domainsLoaded, setDomainsLoaded] = useState(false)

  const getDomains = useCallback(async () => {
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos || [])
        setRegions((res.domainInfos || []).map((info) => info.domain))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setDomainsLoaded(true)
    }
  }, [])

  useEffect(() => {
    getDomains()
  }, [])

  // Use React Query for data fetching with automatic request cancellation
  const { instances, total, isLoading, refetch } = useInstanceListQuery({
    page,
    pageSize,
    statusCodes: filter?.codes,
    regions,
    regionFilterCriteria,
    enabled: domainsLoaded,
  })

  // Reset to first page when filter changes
  useEffect(() => {
    setPage(1)
  }, [filter?.value, regionFilterCriteria, regions.join(',')])

  const onRelease = useCallback(
    async (data: Instance) => {
      await Modal.confirm({
        title: t('instance.releaseModal.title'),
        content: (
          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
            <div>
              <div className="text-[20px] leading-7 font-bold  text-primary">
                {data.displayTitle}
              </div>
            </div>
            <Divider className="my-3" />
            <div>
              <div className="t-sb text-secondary font-medium">
                <Trans i18nKey="instance.releaseModal.content">
                  The machine will be stopped and released, please save the environment if you need
                  it.
                </Trans>
              </div>
              <div
                className=" text-btn-secondary t-sb cursor-pointer"
                onClick={() =>
                  window.open('https://docs.glows.ai/docs/my-instance#1-take-snapshot', '_blank')
                }
              >
                <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                  Why save the environment?
                </Trans>
              </div>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          text: t('instance.releaseModal.stopReleaseBtn'),
        },
        className: 'w-[800px]',
      })
      const res = await API.deleteInstance({ id: data.id })
      if (res.code === 0) {
        message.success(t('common.message.success'))
        refetch()
      }
    },
    [refetch, t],
  )

  const { cancelInstanceSnapshot: onCancelSnapshot } = useCancelSnapshot({ onSuccess: refetch })

  const onUpdate = useCallback(async () => {
    await refetch()
  }, [refetch])

  // Wrapper for getNodeList to maintain backward compatibility
  const getNodeList = useCallback(
    async (p: number, _force: boolean) => {
      if (p !== page) {
        setPage(p)
      } else {
        await refetch()
      }
    },
    [page, refetch],
  )

  return {
    // States
    filter,
    setFilter,
    page,
    setPage,
    total,
    instaceList: instances, // Keep original naming (typo) for backward compatibility
    loading: isLoading || !domainsLoaded,
    domainInfos,
    regions,
    setRegions,
    regionFilterCriteria,
    setRegionFilterCriteria,
    createSnapshotInstance,
    setCreateSnapshotInstance,

    // Methods
    getNodeList,
    onRelease,
    onCancelSnapshot,
    onUpdate,

    // Constants
    statusFilters,
    pageSize,
    REGION_GLOBAL,
    REGION_DATA_DRIVE,
    REGION_MESH_CLUSTERING,
  }
}
